import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { resolveApiError, useTranslation } from '@eon/i18n';
import {
  Badge,
  Button,
  DataTable,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Input,
  PageHeader,
  Pagination,
  SelectField,
  toast,
  type Column,
} from '@eon/ui';
import { hooks, type AdminUser } from '@eon/api-client';
import { formatDate, formatRelative } from '@eon/utils';
import { usePaginationLabels } from '@/lib/use-pagination-labels';
import { useTableSort } from '@/lib/table-sort';
import {
  Eye,
  KeyRound,
  Mail,
  MoreVertical,
  Pencil,
  Search,
  Trash2,
  UserPlus,
} from 'lucide-react';

const PAGE_SIZE_OPTIONS = [25, 50, 100, 200];

const ACTIVE_FILTER = [
  { value: 'all', labelKey: 'filters.all' },
  { value: 'true', labelKey: 'user.active' },
  { value: 'false', labelKey: 'user.inactive' },
] as const;

export function UsersPage() {
  const { t } = useTranslation();
  const labels = usePaginationLabels();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [q, setQ] = useState('');
  const [role, setRole] = useState<string | undefined>(undefined);
  const [appType, setAppType] = useState<string | undefined>(undefined);
  const [activeFilter, setActiveFilter] = useState<string>('all');

  const { sort, onSortChange, sortParams } = useTableSort(() => setPage(1));
  const list = hooks.useAdminUsers({
    page,
    pageSize,
    q: q.trim() || undefined,
    role,
    appType,
    active: activeFilter === 'all' ? undefined : activeFilter,
    ...sortParams,
  });
  const del = hooks.useDeleteUser();
  const forgot = hooks.useForgotPassword();

  const roleFilterOptions = [
    { value: '__all__', label: t('filters.all') },
    ...['system_admin', 'technical', 'user', 'member', 'admin', 'manager', 'owner'].map((r) => ({
      value: r,
      label: t(`user.role.${r}`),
    })),
  ];

  const appFilterOptions = [
    { value: '__all__', label: t('filters.all') },
    ...['system', 'organization', 'customer'].map((a) => ({
      value: a,
      label: t(`user.appType.${a}`),
    })),
  ];

  const activeOptions = ACTIVE_FILTER.map((f) => ({
    value: f.value,
    label: t(f.labelKey),
  }));

  const onDelete = (user: AdminUser) => {
    const label = [user.name, user.surname].filter(Boolean).join(' ') || user.email || user.id;
    if (!window.confirm(label)) return;
    del.mutate(user.id, {
      onSuccess: () => toast.success(t('actions.delete')),
      onError: (e) => toast.error(resolveApiError(e, t)),
    });
  };

  const onPasswordReset = (user: AdminUser) => {
    const email = user.email?.trim();
    if (!email) {
      toast.error(t('user.resetPassword.noEmail'));
      return;
    }
    if (!window.confirm(`${t('user.resetPassword.confirm')} (${email})`)) return;
    forgot.mutate(
      { email },
      {
        onSuccess: () => toast.success(t('user.resetPassword.sent')),
        onError: (e) => toast.error(resolveApiError(e, t)),
      },
    );
  };

  const siteNames = (user: AdminUser) => {
    const names = (user.organizations ?? []).map((o) => o.name);
    return names.length ? names.join(', ') : '—';
  };

  const columns: Column<AdminUser>[] = [
    {
      key: 'name',
      header: t('user.name'),
      sortable: true,
      sortValue: (u) => [u.name, u.surname].filter(Boolean).join(' '),
      cell: (u) => (
        <div className="flex flex-col">
          <Link to="/users/$userId" params={{ userId: u.id }} className="font-medium hover:underline">
            {[u.name, u.surname].filter(Boolean).join(' ') || '—'}
          </Link>
          <Badge variant={u.active ? 'success' : 'secondary'} className="mt-1 w-fit text-[10px]">
            {u.active ? t('user.active') : t('user.inactive')}
          </Badge>
        </div>
      ),
    },
    {
      key: 'email',
      header: t('user.email'),
      sortable: true,
      cell: (u) => <span className="font-mono text-xs">{u.email ?? '—'}</span>,
    },
    {
      key: 'phone',
      header: t('user.phone'),
      sortable: true,
      cell: (u) => u.phone ?? '—',
    },
    {
      key: 'sites',
      header: t('user.groups.label'),
      cell: (u) => <span className="text-muted-foreground text-sm">{siteNames(u)}</span>,
    },
    {
      key: 'role',
      header: t('user.role.label'),
      sortable: true,
      cell: (u) => {
        const key = `user.role.${u.role}`;
        const label = t(key);
        return <Badge variant="secondary">{label === key ? u.role : label}</Badge>;
      },
    },
    {
      key: 'app',
      header: t('user.appType.label'),
      sortable: true,
      sortKey: 'app',
      sortValue: (u) => u.appType,
      cell: (u) => {
        const key = `user.appType.${u.appType}`;
        const label = t(key);
        return <Badge variant="outline">{label === key ? u.appType : label}</Badge>;
      },
    },
    {
      key: 'lastLogin',
      header: t('device.lastSeen'),
      sortable: true,
      sortKey: 'lastLogin',
      sortValue: (u) => u.lastLoginAt,
      cell: (u) => (
        <span className="text-muted-foreground text-xs">{formatRelative(u.lastLoginAt)}</span>
      ),
    },
    {
      key: 'created',
      header: t('user.createdAt'),
      sortable: true,
      sortValue: (u) => u.createdAt,
      cell: (u) => formatDate(u.createdAt),
    },
    {
      key: 'actions',
      header: '',
      className: 'w-12 text-right',
      cell: (u) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon">
              <MoreVertical className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem asChild>
              <Link to="/users/$userId" params={{ userId: u.id }}>
                <Eye className="mr-2 h-4 w-4" />
                {t('user.actions.view')}
              </Link>
            </DropdownMenuItem>
            <DropdownMenuItem asChild>
              <Link to="/users/$userId/edit" params={{ userId: u.id }}>
                <Pencil className="mr-2 h-4 w-4" />
                {t('actions.edit')}
              </Link>
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => onPasswordReset(u)}>
              <KeyRound className="mr-2 h-4 w-4" />
              {t('user.resetPassword.action')}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem className="text-destructive" onClick={() => onDelete(u)}>
              <Trash2 className="mr-2 h-4 w-4" />
              {t('actions.delete')}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
  ];

  const totalCount = list.data?.totalCount ?? 0;
  const totalPages = list.data?.totalPages ?? 1;

  return (
    <>
      <PageHeader
        title={t('nav.users')}
        description={
          list.data ? `${totalCount.toLocaleString()} ${t('nav.users')}` : undefined
        }
        actions={
          <>
            <Button variant="outline" asChild>
              <Link to="/users/invite">
                <Mail className="mr-2 h-4 w-4" />
                {t('user.invite.action')}
              </Link>
            </Button>
            <Button asChild>
              <Link to="/users/new">
                <UserPlus className="mr-2 h-4 w-4" />
                {t('user.createTitle')}
              </Link>
            </Button>
          </>
        }
      />

      <div className="mb-4 flex flex-col gap-3 lg:flex-row lg:items-center">
        <div className="relative max-w-md flex-1">
          <Search className="text-muted-foreground absolute top-2.5 left-3 h-4 w-4" />
          <Input
            className="pl-9"
            placeholder={t('actions.search')}
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
              setPage(1);
            }}
          />
        </div>
        <SelectField
          className="w-full lg:w-44"
          value={role ?? '__all__'}
          onValueChange={(v) => {
            setRole(v === '__all__' ? undefined : v);
            setPage(1);
          }}
          options={roleFilterOptions}
          placeholder={t('user.role.label')}
        />
        <SelectField
          className="w-full lg:w-44"
          value={appType ?? '__all__'}
          onValueChange={(v) => {
            setAppType(v === '__all__' ? undefined : v);
            setPage(1);
          }}
          options={appFilterOptions}
          placeholder={t('user.appType.label')}
        />
        <SelectField
          className="w-full lg:w-36"
          value={activeFilter}
          onValueChange={(v) => {
            setActiveFilter(v);
            setPage(1);
          }}
          options={activeOptions}
        />
      </div>

      <DataTable
        data={list.data?.data}
        columns={columns}
        loading={list.isLoading}
        rowKey={(u) => u.id}
        emptyTitle={t('user.notFound')}
        emptyDescription={t('user.emptyDescription')}
        sort={sort}
        onSortChange={onSortChange}
      />

      {list.data && totalCount > 0 ? (
        <Pagination
          page={page}
          totalPages={totalPages}
          totalCount={totalCount}
          pageSize={pageSize}
          pageSizeOptions={PAGE_SIZE_OPTIONS}
          loading={list.isFetching}
          onPageChange={setPage}
          onPageSizeChange={(size) => {
            setPageSize(size);
            setPage(1);
          }}
          labels={labels}
        />
      ) : null}
    </>
  );
}
